import CharacterCount from '@tiptap/extension-character-count';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import Youtube from '@tiptap/extension-youtube';
import { type Extensions } from '@tiptap/react';
import StarterKit, { type StarterKitOptions } from '@tiptap/starter-kit';
import type { IntlShape } from 'react-intl';

import { Heading } from './custom-heading/custom-heading';
import { XingImage } from './xing-image';
import { XingQuote } from './xing-quote';
import { XingVideo } from './xing-video';

export type ExtensionsOptions = Partial<{
  intl: IntlShape;
  contentPlaceholder: string;
  maxCharacterLimit: number;
  starterKitOptions: Partial<StarterKitOptions>;
}>;

export const getExtensions = (options?: ExtensionsOptions): Extensions => {
  return [
    StarterKit.configure({
      gapcursor: false,
      ...options?.starterKitOptions,
      heading: false,
    }),
    Heading.configure({
      ...options?.starterKitOptions?.heading,
    }),
    Link.configure({
      autolink: false,
      HTMLAttributes: {
        rel: 'noopener noreferrer',
      },
      validate: (href) => /^https?:\/\/[a-zA-Z0-9]+\.[a-zA-Z0-9]+/.test(href),
    }),
    Placeholder.configure({
      includeChildren: true,
      showOnlyCurrent: false,
      placeholder: ({ node }) => {
        if (node.type.name === 'quote') {
          return (
            options?.intl?.formatMessage({
              id: 'ARTICLES_ARTICLE_EDITOR_CONTENT_EDITOR_PLACEHOLDER_QUOTE',
            }) ?? ''
          );
        }
        if (node.type.name === 'quoteCaption') {
          return (
            options?.intl?.formatMessage({
              id: 'ARTICLES_ARTICLE_EDITOR_CONTENT_EDITOR_PLACEHOLDER_QUOTE_SOURCE',
            }) ?? ''
          );
        }
        if (node.type.name === 'imageCaption') {
          return (
            options?.intl?.formatMessage({
              id: 'ARTICLES_ARTICLE_EDITOR_CONTENT_EDITOR_PLACEHOLDER_IMAGE_CAPTION',
            }) ?? ''
          );
        }
        if (node.type.name === 'videoCaption') {
          return (
            options?.intl?.formatMessage({
              id: 'ARTICLES_ARTICLE_EDITOR_CONTENT_EDITOR_PLACEHOLDER_VIDEO_CAPTION',
            }) ?? ''
          );
        }

        if (options?.contentPlaceholder) {
          return options.contentPlaceholder;
        }

        const defaultPlaceholder =
          options?.intl?.formatMessage({
            id: 'ARTICLES_ARTICLE_EDITOR_CONTENT_EDITOR_PLACEHOLDER_CONTENT',
          }) ?? '';

        return defaultPlaceholder;
      },
    }),
    XingImage,
    XingVideo,
    Youtube.configure({
      nocookie: true,
    }),
    CharacterCount.configure({
      limit: options?.maxCharacterLimit || 500000,
    }),
    XingQuote,
  ];
};

export { XingImage, XingQuote, XingVideo };
